import { useEditor } from "features/editor/Feature";
import { usePitcher } from "features/utils/usePitcher";
import { useTasks } from "hooks/pitcher/useTasks";
import { useUnassignedPorts } from "hooks/pitcher/useUnassignedPorts";
import { Box, Stack, Button, Text, Icon, Divider } from "prism-react";
import { Fragment } from "react";

import { executeVSCodeCommand } from "utils/vscode";

export const LayoutPlaceholder: React.FC = () => {
  return (
    <Stack
      css={{
        height: "100%",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
        position: "relative",
      }}
    >
      <Box>
        <Stack css={{ alignItems: "center" }} gap={6}>
          <Box
            css={{
              alignItems: "normal",
              gap: "0 $2",
              width: "100%",
            }}
          >
            <DevToolPlaceholder />
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

const DevToolPlaceholder = () => {
  const tasks = useTasks();
  const unassignedPorts = useUnassignedPorts();
  const tasksWithPorts = Object.values(tasks).filter(
    (t) => t.shell?.status === "RUNNING" && t.ports.length > 0,
  );
  const editor = useEditor();
  const pitcher = usePitcher();

  const hasPortsToShow = tasksWithPorts.length + unassignedPorts.length > 0;

  return (
    <Stack css={{ alignItems: "center" }} gap={4}>
      {hasPortsToShow && (
        <Stack
          css={{
            justifyContent: "center",
            marginBottom: "$4",
            textAlign: "center",
            alignItems: "center",
          }}
        >
          <Stack>
            <Stack css={{ justifyContent: "center" }} horizontal>
              <Icon size={4} type="preview" />
              <Text color="neutral-fg-high">Running previews</Text>
            </Stack>

            {tasksWithPorts.map((task) => (
              <Fragment key={task.id}>
                {task.ports.map((p) => {
                  return (
                    <Button
                      key={`${task.name}:${p.port}`}
                      onClick={() => {
                        executeVSCodeCommand("codesandbox.openCsbTab", {
                          type: "TASK_PORT",
                          port: p.port,
                          taskId: task.id,
                        });
                      }}
                      variant="secondary"
                    >
                      {task.name}:{p.port}
                    </Button>
                  );
                })}
              </Fragment>
            ))}

            {unassignedPorts.map((p) => (
              <Button
                key={`port-${p.port}`}
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "UNASSIGNED_PORT",
                    port: p.port,
                  });
                }}
                variant="secondary"
              >
                {p.port === 0 ? "Preview" : `Preview: ${p.port}`}
              </Button>
            ))}

            {editor.type === "sandbox" && !editor.isCloud && (
              <Button
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "TESTS",
                  });
                }}
                variant="secondary"
              >
                Tests
              </Button>
            )}

            <Divider css={{ marginTop: "$4" }} />
          </Stack>
        </Stack>
      )}

      <Stack gap={0}>
        <Button
          css={{
            width: "min-content",
            justifySelf: "end",
          }}
          icon="pages"
          onClick={() => {
            executeVSCodeCommand("codesandbox.openCsbTab", {
              type: "DOCS",
            });
          }}
        >
          Documentation
        </Button>

        {pitcher.capabilities.task?.runCommand &&
          pitcher.permissions.task?.runCommand && (
            <>
              <Button
                css={{
                  width: "min-content",
                  justifySelf: "end",
                }}
                icon="server"
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "VIRTUAL_MACHINE",
                  });
                }}
              >
                VM Usage & Specs
              </Button>

              <Button
                css={{
                  width: "min-content",
                  justifySelf: "end",
                }}
                icon="server"
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "ENV_SETUP",
                  });
                }}
              >
                Project Setup
              </Button>

              <Button
                css={{
                  width: "min-content",
                  justifySelf: "end",
                }}
                icon="play"
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "SETUP_TASKS",
                  });
                }}
              >
                Run Setup Tasks
              </Button>
            </>
          )}
      </Stack>
    </Stack>
  );
};
